import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import "./CheckinForm.css";
import "./RoomForm.css";
import "./SignoffForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const SAVE_DEBOUNCE_MS = 800;

// "tentant" is the backend's spelling.
const TENANTS = [
  { key: "tenant_1", role: "Tenant 1", field: "signature_tentant_1_blob" },
  { key: "tenant_2", role: "Tenant 2", field: "signature_tentant_2_blob" },
  { key: "tenant_3", role: "Tenant 3", field: "signature_tentant_3_blob" },
];

const WELOCATE_PARTY = {
  key: "welocate",
  role: "Welocate, for the landlord",
  field: "welocate_signature_blob",
};

// The plain fields this page keeps on the check-in itself, under the names the
// server stores them by. The signatures are not among them: they are only sent
// with the sign-off, which is what turns them into part of the record.
const DETAILS = [
  "comments",
  "sign_off_date",
  "sign_off_location",
  "welocate_signer",
];

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

// <input type="date"> wants yyyy-mm-dd in local time — toISOString would hand
// back the UTC day, which is the day before for anyone east of Greenwich.
const toDateField = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
};

// A signature that came back from the server is a blob URL; one drawn here is
// a data URL, and only those are new enough to be worth sending.
const isDrawn = (value) => typeof value === "string" && value.startsWith("data:");

const pointIn = (canvas, event) => {
  const rect = canvas.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
};

const SignaturePad = ({ role, name, onCancel, onSave }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Sized in device pixels, so the stroke is drawn sharp rather than scaled up
  // from a CSS-sized bitmap afterwards.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);

    const context = canvas.getContext("2d");
    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#1a1d23";
  }, []);

  // The pad covers the page; letting the page behind it scroll only moves the
  // canvas out from under the finger drawing on it.
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  const startStroke = (event) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const point = pointIn(canvas, event);

    canvas.setPointerCapture(event.pointerId);
    isDrawingRef.current = true;
    setHasInk(true);

    // A tap with no drag is a dot, not a nothing.
    context.beginPath();
    context.moveTo(point.x, point.y);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const extendStroke = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const point = pointIn(canvas, event);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const endStroke = () => {
    isDrawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    // The context carries the device-pixel scale, so wipe in device pixels.
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.restore();
    setHasInk(false);
  };

  const save = () => {
    const canvas = canvasRef.current;
    // Flattened onto white: a transparent PNG turns into an invisible
    // signature the moment it lands on a dark background or in the PDF.
    const output = document.createElement("canvas");
    output.width = canvas.width;
    output.height = canvas.height;
    const context = output.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, output.width, output.height);
    context.drawImage(canvas, 0, 0);
    onSave(output.toDataURL("image/png"));
  };

  return (
    <div className="sf-pad" role="dialog" aria-modal="true" aria-label={`Sign — ${role}`}>
      <header className="sf-pad-head">
        <div className="sf-pad-titles">
          <span className="sf-pad-role">{role}</span>
          <span className="sf-pad-name">{name}</span>
        </div>
        <button
          type="button"
          className="sf-pad-close"
          aria-label="Close without signing"
          onClick={onCancel}
        >
          <CloseIcon />
        </button>
      </header>

      <div className="sf-pad-sheet">
        <canvas
          ref={canvasRef}
          className="sf-pad-canvas"
          onPointerDown={startStroke}
          onPointerMove={extendStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
        />
        {!hasInk && <span className="sf-pad-hint">Sign here</span>}
        <span className="sf-pad-rule" aria-hidden="true" />
      </div>

      <footer className="sf-pad-actions">
        <button
          type="button"
          className="cf-btn cf-btn--ghost"
          disabled={!hasInk}
          onClick={clear}
        >
          Clear
        </button>
        <button
          type="button"
          className="cf-btn cf-btn--primary"
          disabled={!hasInk}
          onClick={save}
        >
          <CheckIcon />
          Use signature
        </button>
      </footer>
    </div>
  );
};

const SignoffForm = () => {
  const { cfid } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: saveDetailsRequest,
    isLoading: isSaving,
    error: saveError,
  } = useHttpNoAuth();
  const {
    sendRequest: saveSignatureRequest,
    isLoading: isSavingSignature,
    error: signatureError,
  } = useHttpNoAuth();
  const {
    sendRequest: signoffRequest,
    isLoading: isSigningOff,
    error: signoffError,
  } = useHttpNoAuth();

  const [address, setAddress] = useState("");
  // Postal code and city read as one place, the way the check-in header
  // writes them.
  const [place, setPlace] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [isSignedOff, setIsSignedOff] = useState(false);
  // The same form signs off both handovers — every label follows the type the
  // API sends, and anything that is not a check-out is a check-in.
  const [isCheckout, setIsCheckout] = useState(false);
  const typeLabel = isCheckout ? "check-out" : "check-in";

  const [remarks, setRemarks] = useState("");
  const [signedOn, setSignedOn] = useState(() => toDateField(new Date()));
  const [location, setLocation] = useState("");
  const [welocateSigner, setWelocateSigner] = useState("");

  // Party key -> blob URL from the server, or a data URL drawn just now.
  const [signatures, setSignatures] = useState({});
  // Party key -> "saving" | "failed". A key listed here is one the server does
  // not have; anything absent is stored, either as loaded or as a PATCH
  // confirmed it.
  const [signatureState, setSignatureState] = useState({});
  const [signingParty, setSigningParty] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Only the tenants actually on this check-in sign — there can be one, two
  // or three.
  const [tenantNames, setTenantNames] = useState({});

  // What the server currently holds — nothing is sent while the form matches it.
  const baselineRef = useRef(null);

  // The signatures as they were before the save in flight, so a clear the
  // server refused can be put back.
  const signaturesRef = useRef({});
  useEffect(() => {
    signaturesRef.current = signatures;
  }, [signatures]);

  const loadCheckin = useCallback(() => {
    if (!cfid) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        setAddress(
          [data?.street, data?.number, data?.floor].filter(Boolean).join(" ")
        );
        setPlace([data?.postal_code, data?.city].filter(Boolean).join(" "));
        setIsSignedOff(Boolean(data?.is_signed_off));
        setIsCheckout(data?.type === "checkout");

        // Today and the city of the property are where the handover happens
        // often enough to be the starting point rather than empty fields.
        const fetched = {
          comments: data?.comments ?? "",
          sign_off_date: data?.sign_off_date || toDateField(new Date()),
          sign_off_location: data?.sign_off_location || data?.city || "",
          welocate_signer: data?.welocate_signer ?? "",
        };

        setRemarks(fetched.comments);
        setSignedOn(fetched.sign_off_date);
        setLocation(fetched.sign_off_location);
        setWelocateSigner(fetched.welocate_signer);

        // The baseline is what the form was seeded with, defaults included, so
        // the page does not save a date nobody typed the moment it opens.
        baselineRef.current = fetched;

        setTenantNames(
          Object.fromEntries(
            TENANTS.map((tenant) => [tenant.key, data?.[tenant.key] ?? ""])
          )
        );

        setSignatures((current) => {
          const stored = {};
          [WELOCATE_PARTY, ...TENANTS].forEach((party) => {
            // A signature drawn on this page has not been sent yet, so it
            // outranks the empty field the server still has for it.
            if (isDrawn(current[party.key])) {
              stored[party.key] = current[party.key];
              return;
            }
            if (data?.[party.field]) stored[party.key] = data[party.field];
          });
          return stored;
        });

        // Whatever the server just handed back is, by definition, what it
        // holds. Only a signature kept from the page over an empty field
        // stays marked as missing.
        setSignatureState((current) => {
          const next = {};
          [WELOCATE_PARTY, ...TENANTS].forEach((party) => {
            if (!data?.[party.field] && current[party.key]) {
              next[party.key] = current[party.key];
            }
          });
          return next;
        });

        setLoaded(true);
      }
    );
  }, [cfid, getCheckinRequest]);

  useEffect(() => {
    loadCheckin();
  }, [loadCheckin]);

  const parties = useMemo(
    () => [
      WELOCATE_PARTY,
      ...TENANTS.filter((tenant) => Boolean(tenantNames[tenant.key])),
    ],
    [tenantNames]
  );

  const nameFor = useCallback(
    (party) =>
      party.key === WELOCATE_PARTY.key
        ? welocateSigner.trim() || "Welocate"
        : tenantNames[party.key],
    [welocateSigner, tenantNames]
  );

  const details = useMemo(
    () => ({
      comments: remarks,
      sign_off_date: signedOn,
      sign_off_location: location,
      welocate_signer: welocateSigner,
    }),
    [remarks, signedOn, location, welocateSigner]
  );

  // Recomputed every render: the baseline lives in a ref and moves on each save.
  const base = baselineRef.current;
  const isDirty = Boolean(
    base && DETAILS.some((key) => base[key] !== details[key])
  );

  // Resolves to whether the server took it: the request hook reports a failure
  // by setting its error, not by rejecting, so the caller has to be told.
  const saveDetails = useCallback(async () => {
    const sent = details;
    let stored = false;
    await saveDetailsRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}`,
        method: "PATCH",
        // A cleared date field is nothing, not "" — the latter is not a date
        // the server can store.
        body: { ...sent, sign_off_date: sent.sign_off_date || null },
      },
      () => {
        stored = true;
        baselineRef.current = sent;
        setSavedAt(new Date());
      }
    );
    return stored;
  }, [details, cfid, saveDetailsRequest]);

  // A signature is finished the moment the pad closes, so there is nothing to
  // debounce: it goes up on its own, under the column it belongs to. Clearing
  // one sends null, which is how the field is emptied again.
  const saveSignature = useCallback(
    async (party, dataUrl) => {
      const previous = signaturesRef.current[party.key];

      setSignatures((current) => {
        const next = { ...current };
        if (dataUrl) next[party.key] = dataUrl;
        else delete next[party.key];
        return next;
      });
      setSignatureState((current) => ({ ...current, [party.key]: "saving" }));

      let stored = false;
      await saveSignatureRequest(
        {
          url: `${BACKEND_URL}/welocate/checkins/${cfid}`,
          method: "PATCH",
          body: { [party.field]: dataUrl ?? null },
        },
        () => {
          stored = true;
          setSavedAt(new Date());
        }
      );

      // A clear the server refused leaves the signature on the record, so it
      // goes back on screen rather than the page showing it gone.
      if (!stored && !dataUrl && previous) {
        setSignatures((current) => ({ ...current, [party.key]: previous }));
      }

      setSignatureState((current) => {
        const next = { ...current };
        // A failed clear ends with the server and the page agreeing again; a
        // failed save leaves ink the record does not have.
        if (stored || !dataUrl) delete next[party.key];
        else next[party.key] = "failed";
        return next;
      });
    },
    [cfid, saveSignatureRequest]
  );

  // Autosave: these fields belong to the check-in, not to the sign-off, so they
  // are kept whether or not this page ends in a signature.
  useEffect(() => {
    if (!isDirty || isSignedOff) return undefined;
    const timer = setTimeout(saveDetails, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDirty, isSignedOff, saveDetails]);

  // Only what the server confirmed counts. Ink that is still in flight, or
  // that a failed PATCH left on the page, is not part of the record yet, and
  // signing off on it would close the check-in without that signature.
  const signed = parties.filter(
    (party) => signatures[party.key] && !signatureState[party.key]
  ).length;

  const canSignOff =
    !isSignedOff &&
    signed === parties.length &&
    Boolean(signedOn) &&
    Boolean(location.trim()) &&
    Boolean(welocateSigner.trim());

  const submit = async () => {
    if (!canSignOff || isSigningOff) return;
    // The date, location and signer go up first; if that PATCH fails the
    // sign-off would be recorded against details the server never took.
    if (isDirty && !(await saveDetails())) return;

    signoffRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}/sign-off`,
        method: "POST",
        body: {
          signed_on: signedOn,
          signed_location: location.trim(),
          welocate_signer: welocateSigner.trim(),
          // One field per signature, named after the column it lands in. A
          // tenant the property does not have is left out entirely rather
          // than sent empty.
          ...Object.fromEntries(
            parties.map((party) => [party.field, signatures[party.key]])
          ),
        },
      },
      () => navigate(`/checkin/${cfid}`)
    );
  };

  const isBusy = isSaving || isSavingSignature;
  const hasFailed = !isBusy && Boolean(saveError || signatureError);

  // Edits are only in the browser until the debounce fires and the PATCH
  // lands, so the way out stays shut until then. A failed save reopens it —
  // the error is on screen and stranding the user on the page helps nobody.
  const isSavePending = (isDirty || isBusy) && !hasFailed;

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        <button
          type="button"
          className="cf-back"
          aria-label={isSavePending ? "Saving — please wait" : "Back"}
          aria-busy={isSavePending}
          disabled={isSavePending}
          onClick={() => navigate(`/checkin/${cfid}`)}
        >
          {isSavePending ? <span className="cf-spinner" /> : <ChevronLeftIcon />}
        </button>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">Sign-off</h1>
          {/* A record missing either part keeps what it has on its own,
              rather than trailing a separator with nothing after it. */}
          <p className="cf-topbar-sub">
            {[address, place].filter(Boolean).join(" · ")}
          </p>
        </div>

        {(isBusy || hasFailed || savedAt) && (
          <span
            className={`cf-saved${hasFailed ? " rf-saved--error" : ""}`}
            role="status"
          >
            {isBusy && "Saving…"}
            {hasFailed && "Not saved"}
            {!isBusy && !hasFailed && (
              <>
                <CheckCircleOutlineIcon />
                Saved <span className="cf-saved-time">{formatTime(savedAt)}</span>
              </>
            )}
          </span>
        )}
      </header>

      {isLoading && !loaded && <p className="cf-state">Loading sign-off…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={loadCheckin}>
            Try again
          </button>
        </div>
      )}

      {loaded && (
        <main className="cf-main">
          {isSignedOff && (
            <p className="cf-locked" role="status">
              <LockOutlinedIcon />
              This {typeLabel} has been signed off. Nothing here can be changed.
            </p>
          )}

          <section className="cf-card rf-block">
            <label className="rf-label" htmlFor="sf-remarks">
              General remarks
            </label>
            <textarea
              id="sf-remarks"
              className="rf-notes"
              placeholder="Anything not tied to one room…"
              value={remarks}
              disabled={isSignedOff}
              onChange={(event) => setRemarks(event.target.value)}
            />
            <p className="rf-help">
              Defects not noted here may still be reported within 14 days.
            </p>

            <label className="rf-label" htmlFor="sf-date">
              Date
            </label>
            <input
              id="sf-date"
              className="rf-name"
              type="date"
              value={signedOn}
              disabled={isSignedOff}
              onChange={(event) => setSignedOn(event.target.value)}
            />

            <label className="rf-label" htmlFor="sf-location">
              Location
            </label>
            <input
              id="sf-location"
              className="rf-name"
              type="text"
              autoComplete="address-level2"
              placeholder="Where the handover takes place"
              value={location}
              disabled={isSignedOff}
              onChange={(event) => setLocation(event.target.value)}
            />

            <label className="rf-label" htmlFor="sf-signer">
              Welocate representative
            </label>
            <input
              id="sf-signer"
              className="rf-name"
              type="text"
              autoComplete="name"
              placeholder="Who is signing for the landlord"
              value={welocateSigner}
              disabled={isSignedOff}
              onChange={(event) => setWelocateSigner(event.target.value)}
            />
          </section>

          <div className="cf-section-head">
            <h3 className="cf-section-title">DECLARATION</h3>
          </div>

          <section className="cf-card sf-declaration">
            <p className="sf-declaration-text">
              The tenant declares that the property has been provided to the
              landlord as described in the current form, that the property is in
              good condition, with no defects except those specified in this
              form. The landlord may report damage or omissions within 14 days
              after the check-out inspection, based on the final condition of
              the property.
            </p>
            <p className="sf-declaration-text">
              The tenant acknowledges and agrees that any damages identified
              during the check-out inspection, or reported by the landlord
              within the 14-day period referred to above, may be deducted from
              the security deposit, to the extent permitted under the tenancy
              agreement and applicable law. The tenant further acknowledges and
              agrees that quotations and/or invoices for the necessary repairs
              or replacements will be provided for full transparency and to
              substantiate any deductions made from the security deposit.
            </p>
          </section>

          <div className="cf-section-head">
            <h3 className="cf-section-title">SIGNATURES</h3>
            <span className="cf-section-hint">
              {signed} of {parties.length} signed
            </span>
          </div>

          {parties.map((party) => {
            const signature = signatures[party.key];
            const state = signatureState[party.key];
            const isDone = Boolean(signature) && state !== "failed";
            return (
              <section
                key={party.key}
                className={`sf-sign${isDone ? " sf-sign--done" : ""}`}
              >
                <span className="sf-sign-role">{party.role}</span>
                <span className="sf-sign-name">{nameFor(party)}</span>

                {signature ? (
                  <>
                    <img
                      className="sf-sign-image"
                      src={signature}
                      alt={`Signature of ${nameFor(party)}`}
                    />
                    {!isSignedOff && (
                      <div className="sf-sign-actions">
                        <button
                          type="button"
                          className="sf-sign-link"
                          onClick={() => setSigningParty(party)}
                        >
                          <EditOutlinedIcon />
                          Sign again
                        </button>
                        <button
                          type="button"
                          className="sf-sign-link sf-sign-link--clear"
                          onClick={() => saveSignature(party, null)}
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="sf-sign-cta"
                    disabled={isSignedOff}
                    onClick={() => setSigningParty(party)}
                  >
                    <EditOutlinedIcon />
                    Tap to sign
                  </button>
                )}

                {state === "saving" && (
                  <span className="sf-sign-state" role="status">
                    Saving…
                  </span>
                )}

                {state === "failed" && (
                  <span className="sf-sign-state sf-sign-state--error" role="alert">
                    Not saved — sign again before signing off.
                  </span>
                )}
              </section>
            );
          })}

          <p className="cf-note">
            Signing closes the {typeLabel}: the rooms, meters and keys are fixed
            as they stand and the report becomes the record of the handover.
          </p>

          {!isSaving && saveError && (
            <p className="rf-error" role="alert">
              {saveError}
            </p>
          )}

          {!isSavingSignature && signatureError && (
            <p className="rf-error" role="alert">
              {signatureError}
            </p>
          )}

          {!isSigningOff && signoffError && (
            <p className="rf-error" role="alert">
              {signoffError}
            </p>
          )}
        </main>
      )}

      {loaded && !isSignedOff && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              disabled={!canSignOff || isSigningOff}
              onClick={submit}
            >
              {isSigningOff ? (
                <span className="cf-spinner rf-btn-spinner" />
              ) : (
                <CheckIcon />
              )}
              {isSigningOff ? "Signing off…" : "Sign off"}
            </button>
          </div>
        </footer>
      )}

      {signingParty && (
        <SignaturePad
          role={signingParty.role}
          name={nameFor(signingParty)}
          onCancel={() => setSigningParty(null)}
          onSave={(dataUrl) => {
            saveSignature(signingParty, dataUrl);
            setSigningParty(null);
          }}
        />
      )}
    </div>
  );
};

export default SignoffForm;
